/**
 * Homepage Scroll Animations
 * Uses GSAP with ScrollTrigger for smooth, minimal animations
 */

(function() {
  'use strict';

  // Wait for GSAP to load
  function initAnimations() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.warn('GSAP or ScrollTrigger not loaded');
      return;
    }

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return;
    }

    // Skip animations in Shopify editor
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    // Default animation settings
    const defaultEase = 'power2.out';
    const defaultDuration = 0.8;

    // ========================================
    // Brand Statement / Text Sections
    // ========================================
    const textSections = document.querySelectorAll('.brand-statement, [class*="statement"], .rich-text, [class*="text-section"]');
    textSections.forEach(section => {
      const heading = section.querySelector('h2, h3, .heading');
      const text = section.querySelector('p, .text, .description');
      const button = section.querySelector('a, button, .button');

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          once: true
        }
      });

      if (heading) {
        gsap.set(heading, { y: 30, opacity: 0 });
        tl.to(heading, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0);
      }

      if (text) {
        gsap.set(text, { y: 25, opacity: 0 });
        tl.to(text, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0.15);
      }

      if (button) {
        gsap.set(button, { y: 20, opacity: 0 });
        tl.to(button, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0.3);
      }
    });

    // ========================================
    // Latest Drop Section - Enhanced Animation
    // ========================================
    const latestDropSection = document.querySelector('.latest-drop');
    if (latestDropSection) {
      // Get all elements to animate
      const ldTitle = latestDropSection.querySelector('.latest-drop__title');
      const ldSubtitle = latestDropSection.querySelector('.latest-drop__subtitle');
      const ldViewAll = latestDropSection.querySelector('.latest-drop__view-all');
      const ldProducts = latestDropSection.querySelectorAll('.latest-drop__product');

      // Check if section is already in viewport (for back navigation)
      const rect = latestDropSection.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

      if (isInViewport) {
        // Section already visible - no animation needed, just ensure elements are visible
        if (ldTitle) gsap.set(ldTitle, { y: 0, opacity: 1 });
        if (ldSubtitle) gsap.set(ldSubtitle, { y: 0, opacity: 1 });
        if (ldViewAll) gsap.set(ldViewAll, { y: 0, opacity: 1 });
        if (ldProducts.length > 0) gsap.set(ldProducts, { y: 0, opacity: 1 });
      } else {
        // Section not in viewport - set up scroll animation
        const ldTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: latestDropSection,
            start: 'top 80%',
            once: true
          }
        });

        if (ldTitle) {
          gsap.set(ldTitle, { y: 30, opacity: 0 });
          ldTimeline.to(ldTitle, { y: 0, opacity: 1, duration: 0.6, ease: defaultEase }, 0);
        }

        if (ldSubtitle) {
          gsap.set(ldSubtitle, { y: 20, opacity: 0 });
          ldTimeline.to(ldSubtitle, { y: 0, opacity: 1, duration: 0.6, ease: defaultEase }, 0.1);
        }

        if (ldViewAll) {
          gsap.set(ldViewAll, { y: 20, opacity: 0 });
          ldTimeline.to(ldViewAll, { y: 0, opacity: 1, duration: 0.6, ease: defaultEase }, 0.15);
        }

        if (ldProducts.length > 0) {
          gsap.set(ldProducts, { y: 50, opacity: 0 });
          ldTimeline.to(ldProducts, {
            y: 0,
            opacity: 1,
            duration: 0.7,
            ease: 'power3.out',
            stagger: 0.15
          }, 0.2);
        }
      }
    }

    // ========================================
    // Media Banner - Text Highlight Animation
    // ========================================
    const mediaBannerHighlight = document.querySelector('.media-banner__highlight[data-highlight-text]');
    if (mediaBannerHighlight) {
      const mediaBanner = mediaBannerHighlight.closest('.media-banner');

      // Check if section is already in viewport (for back navigation)
      const mbRect = mediaBanner.getBoundingClientRect();
      const mbIsInViewport = mbRect.top < window.innerHeight && mbRect.bottom > 0;

      if (mbIsInViewport) {
        // Already in viewport - show highlight immediately
        mediaBannerHighlight.classList.add('is-visible');
      } else {
        // Set up scroll-triggered animation
        ScrollTrigger.create({
          trigger: mediaBanner,
          start: 'top 70%',
          once: true,
          onEnter: () => {
            mediaBannerHighlight.classList.add('is-visible');
          }
        });
      }
    }

    // ========================================
    // Story Banner - Title Highlight Animation
    // ========================================
    const storyBannerHighlight = document.querySelector('.story-banner__highlight[data-highlight-text]');
    if (storyBannerHighlight) {
      const storyBanner = storyBannerHighlight.closest('.story-banner');

      // Check if section is already in viewport (for back navigation)
      const sbRect = storyBanner.getBoundingClientRect();
      const sbIsInViewport = sbRect.top < window.innerHeight && sbRect.bottom > 0;

      if (sbIsInViewport) {
        // Already in viewport - show highlight immediately
        storyBannerHighlight.classList.add('is-visible');
      } else {
        // Set up scroll-triggered animation
        ScrollTrigger.create({
          trigger: storyBanner,
          start: 'top 70%',
          once: true,
          onEnter: () => {
            storyBannerHighlight.classList.add('is-visible');
          }
        });
      }
    }

    // ========================================
    // Our Story Video - Title Highlight Animation
    // ========================================
    const osvHighlight = document.querySelector('.osv__highlight[data-highlight-text]');
    if (osvHighlight) {
      const osvSection = osvHighlight.closest('.our-story-video');

      // Check if section is already in viewport (for back navigation)
      const osvRect = osvSection.getBoundingClientRect();
      const osvIsInViewport = osvRect.top < window.innerHeight && osvRect.bottom > 0;

      if (osvIsInViewport) {
        // Already in viewport - show highlight immediately
        osvHighlight.classList.add('is-visible');
      } else {
        // Set up scroll-triggered animation
        ScrollTrigger.create({
          trigger: osvSection,
          start: 'top 70%',
          once: true,
          onEnter: () => {
            osvHighlight.classList.add('is-visible');
          }
        });
      }
    }

    // ========================================
    // Product Grid / Collection Animations (Other sections)
    // ========================================
    const productGrids = document.querySelectorAll('.collection-list, .product-grid, [class*="product-list"], [class*="collection-grid"], .featured-collection, [class*="more-from-"], .featured-product-slider');
    productGrids.forEach(grid => {
      // Skip latest-drop as it has its own animation
      if (grid.classList.contains('latest-drop')) return;

      // Animate section heading/header first
      const sectionHeading = grid.querySelector('.featured-collection__header, .more-from__header, h2, .section-header, .collection__title, .more-from__title');
      if (sectionHeading) {
        gsap.set(sectionHeading, { y: 30, opacity: 0 });
        gsap.to(sectionHeading, {
          y: 0,
          opacity: 1,
          duration: defaultDuration,
          ease: defaultEase,
          scrollTrigger: {
            trigger: sectionHeading,
            start: 'top 85%',
            once: true
          }
        });
      }

      // Animate individual product cards with stagger (including Bomencia custom classes)
      const productCards = grid.querySelectorAll('.product-card, .card, .collection-product-card, [class*="product-item"], .grid__item, .more-from__product, .featured-product-slider__product');
      if (productCards.length > 0) {
        gsap.set(productCards, { y: 50, opacity: 0 });
        gsap.to(productCards, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: 'power3.out',
          stagger: 0.15,
          scrollTrigger: {
            trigger: grid,
            start: 'top 80%',
            once: true
          }
        });
      }
    });

    // ========================================
    // Slider / Carousel Animations
    // ========================================
    const sliders = document.querySelectorAll('.slider, .swiper, [class*="carousel"], [class*="slider"], .featured-product-slider, .marquee');
    sliders.forEach(slider => {
      const sliderItems = slider.querySelectorAll('.slider__slide, .swiper-slide, [class*="slide"], .product-card');

      if (sliderItems.length > 0) {
        gsap.set(sliderItems, { x: 30, opacity: 0 });
        gsap.to(sliderItems, {
          x: 0,
          opacity: 1,
          duration: 0.6,
          ease: defaultEase,
          stagger: 0.08,
          scrollTrigger: {
            trigger: slider,
            start: 'top 80%',
            once: true
          }
        });
      }
    });

    // ========================================
    // Image Banner / Media Sections
    // ========================================
    const imageBanners = document.querySelectorAll('.image-banner, .media-banner, [class*="story-banner"], .image-with-text');
    imageBanners.forEach(banner => {
      const image = banner.querySelector('img, picture, video');
      const content = banner.querySelector('.banner__content, .image-with-text__content, .story-banner__content, [class*="content"]');
      const heading = banner.querySelector('h2, h3, .heading, .story-banner__title');
      const text = banner.querySelector('p, .text, .story-banner__description');
      const button = banner.querySelector('a.button, a.btn, button, .story-banner__btn');

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: banner,
          start: 'top 75%',
          once: true
        }
      });

      if (image) {
        gsap.set(image, { scale: 1.05, opacity: 0 });
        tl.to(image, { scale: 1, opacity: 1, duration: 1, ease: 'power2.out' }, 0);
      }

      if (heading) {
        gsap.set(heading, { y: 30, opacity: 0 });
        tl.to(heading, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0.2);
      }

      if (text) {
        gsap.set(text, { y: 25, opacity: 0 });
        tl.to(text, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0.35);
      }

      if (button) {
        gsap.set(button, { y: 20, opacity: 0 });
        tl.to(button, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0.5);
      }
    });

    // ========================================
    // Our Story Video Section
    // ========================================
    const videoSection = document.querySelector('.our-story-video, [class*="video-section"]');
    if (videoSection) {
      const videoHeader = videoSection.querySelector('.osv__header, .video__header');
      const videoContainer = videoSection.querySelector('.osv__video-container, .video-container, video');
      const videoBottom = videoSection.querySelector('.osv__bottom, .video__content');

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: videoSection,
          start: 'top 75%',
          once: true
        }
      });

      if (videoHeader) {
        const headerElements = videoHeader.querySelectorAll('h2, h3, p, span');
        gsap.set(headerElements, { y: 25, opacity: 0 });
        tl.to(headerElements, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase, stagger: 0.1 }, 0);
      }

      if (videoContainer) {
        gsap.set(videoContainer, { y: 30, opacity: 0 });
        tl.to(videoContainer, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0.2);
      }

      if (videoBottom) {
        const bottomElements = videoBottom.querySelectorAll('h3, p, span, a');
        gsap.set(bottomElements, { y: 20, opacity: 0 });
        tl.to(bottomElements, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase, stagger: 0.1 }, 0.4);
      }
    }

    // ========================================
    // Marquee Animation Enhancement
    // ========================================
    const marquees = document.querySelectorAll('.marquee, [class*="marquee-slider"]');
    marquees.forEach(marquee => {
      gsap.set(marquee, { opacity: 0 });
      gsap.to(marquee, {
        opacity: 1,
        duration: 0.8,
        ease: defaultEase,
        scrollTrigger: {
          trigger: marquee,
          start: 'top 85%',
          once: true
        }
      });
    });

    // ========================================
    // Generic Section Fade In
    // ========================================
    const genericSections = document.querySelectorAll('.shopify-section:not([data-anim-init])');
    genericSections.forEach(section => {
      // Mark as initialized to prevent double animation
      section.setAttribute('data-anim-init', 'true');

      // Skip if section already has animations applied
      if (section.querySelector('[style*="opacity"]')) return;

      const sectionContent = section.querySelector('.page-width, .container, [class*="container"]');
      if (sectionContent && !sectionContent.closest('.hero-banner, .product-grid, .collection-list, .slider, .marquee, .image-banner')) {
        gsap.set(sectionContent, { y: 30, opacity: 0 });
        gsap.to(sectionContent, {
          y: 0,
          opacity: 1,
          duration: defaultDuration,
          ease: defaultEase,
          scrollTrigger: {
            trigger: section,
            start: 'top 85%',
            once: true
          }
        });
      }
    });

    // ========================================
    // Parallax Effect for Images (optional)
    // ========================================
    const parallaxImages = document.querySelectorAll('[data-parallax]');
    parallaxImages.forEach(img => {
      gsap.to(img, {
        yPercent: -15,
        ease: 'none',
        scrollTrigger: {
          trigger: img.closest('.shopify-section') || img,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1
        }
      });
    });

    // Refresh ScrollTrigger after images load
    window.addEventListener('load', () => {
      ScrollTrigger.refresh();
    });

    // Refresh on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 250);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAnimations);
  } else {
    initAnimations();
  }

  // Also try after a short delay in case GSAP loads async
  setTimeout(initAnimations, 100);
})();
